/**
 * Global axios interceptor that consumes structured error codes from the
 * backend (see backend/services/app_errors.py) and shows smart toasts.
 *
 * Backend response shape on AppError:
 *   { detail: string, error_code: "AUTH-EMAIL-TAKEN", error_details: {...} }
 *
 * Behavior:
 * - If the error has an `error_code`, look up a custom toast handler for it.
 * - If a handler exists, show its custom toast (may include action buttons).
 * - Otherwise, fall through — the calling code's own catch handles it as
 *   before (backwards compat with existing `err.response.data.detail` reads).
 * - Errors are always re-thrown so existing `try/catch` blocks still run.
 *
 * Opt-out per request: set `config.meta.skipErrorToast = true` on the
 * axios call to disable the interceptor for that specific request. Useful
 * for forms that render their own inline validation messages.
 */
import axios from 'axios';
import { toast } from 'sonner';

// Map error_code → toast renderer. Each entry returns a config object
// passed to sonner's toast. Return null to skip auto-toasting.
const ERROR_HANDLERS = {
  'AUTH-EMAIL-TAKEN': (err) => ({
    kind: 'error',
    message: 'That email is already registered.',
    description: 'Try signing in instead — or use a different email.',
    action: {
      label: 'Sign in',
      onClick: () => {
        window.location.href = '/login';
      },
    },
  }),

  'AUTH-USERNAME-TAKEN': () => ({
    kind: 'error',
    message: 'Username already taken.',
    description: 'Pick a different username and try again.',
  }),

  'AUTH-INVALID-CREDENTIALS': () => ({
    kind: 'error',
    message: 'Email or password is incorrect.',
    description: 'Double-check your credentials or reset your password.',
  }),

  'AUTH-CAPTCHA-FAILED': () => ({
    kind: 'error',
    message: 'Human verification failed.',
    description: 'Please refresh the page and try the CAPTCHA again.',
  }),

  'AUTH-PASSWORD-WEAK': (err) => ({
    kind: 'error',
    message: 'Password too weak.',
    description: err.response?.data?.detail || 'Use at least 8 characters.',
  }),

  'AUTH-RESET-TOKEN-INVALID': () => ({
    kind: 'error',
    message: 'Reset link is invalid.',
    description: 'Request a new password reset email.',
    action: {
      label: 'Reset password',
      onClick: () => {
        window.location.href = '/forgot-password';
      },
    },
  }),

  'AUTH-RESET-TOKEN-EXPIRED': () => ({
    kind: 'error',
    message: 'Reset link expired.',
    description: 'Password reset links expire after 1 hour. Request a new one.',
    action: {
      label: 'Reset password',
      onClick: () => {
        window.location.href = '/forgot-password';
      },
    },
  }),

  'AUTH-NOT-AUTHENTICATED': () => ({
    kind: 'error',
    message: 'You need to sign in first.',
    action: {
      label: 'Sign in',
      onClick: () => {
        window.location.href = '/login';
      },
    },
  }),

  'AUTH-INSUFFICIENT-PERMISSIONS': () => ({
    kind: 'error',
    message: 'You don\u2019t have permission to do that.',
    description: 'Contact an admin if you believe this is a mistake.',
  }),

  'PROMO-CODE-INVALID': () => ({
    kind: 'error',
    message: 'Promo code is invalid.',
    description: 'Double-check the spelling — codes are case-sensitive.',
  }),

  'PROMO-CODE-EXPIRED': () => ({
    kind: 'error',
    message: 'Promo code has expired.',
  }),

  'PROMO-CODE-EXHAUSTED': () => ({
    kind: 'error',
    message: 'Promo code has been fully redeemed.',
    description: 'This code has reached its usage limit.',
  }),

  'MEMBERSHIP-ALREADY-ACTIVE': () => ({
    kind: 'info',
    message: 'You already have an active membership.',
  }),

  'UPLOAD-FILE-TOO-LARGE': (err) => ({
    kind: 'error',
    message: 'File too large.',
    description: err.response?.data?.detail || 'Try a smaller image.',
  }),

  'UPLOAD-INVALID-TYPE': (err) => {
    const allowed = err.response?.data?.error_details?.allowed_extensions;
    return {
      kind: 'error',
      message: 'File type not allowed.',
      description: allowed
        ? `Allowed types: ${allowed.join(', ')}`
        : 'Please use a supported image format.',
    };
  },

  'UPLOAD-RATE-LIMITED': () => ({
    kind: 'error',
    message: 'Too many uploads.',
    description: 'Please wait a bit before uploading again.',
  }),

  'NGO-DUPLICATE': () => ({
    kind: 'info',
    message: 'This NGO is already listed.',
    description: 'Check your email — we may have already contacted you about it.',
  }),

  'NGO-NOT-FOUND': () => ({
    kind: 'error',
    message: 'NGO not found.',
  }),

  'NGO-NOT-APPROVED': () => ({
    kind: 'info',
    message: 'This NGO isn\u2019t live yet.',
    description: 'Come back after we\u2019ve approved the listing.',
  }),

  'RATE-LIMITED': () => ({
    kind: 'error',
    message: 'Too many requests — slow down.',
    description: 'Try again in a moment.',
  }),
};

function renderToast(cfg) {
  const opts = {
    description: cfg.description,
    action: cfg.action,
    duration: cfg.duration || 6000,
  };
  if (cfg.kind === 'info') {
    toast.info(cfg.message, opts);
  } else if (cfg.kind === 'success') {
    toast.success(cfg.message, opts);
  } else {
    toast.error(cfg.message, opts);
  }
}

let installed = false;

export function installApiErrorInterceptor() {
  if (installed) return;
  installed = true;

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      // Respect per-request opt-out
      if (error?.config?.meta?.skipErrorToast) {
        return Promise.reject(error);
      }
      const data = error?.response?.data;
      const code = data?.error_code;
      if (code && ERROR_HANDLERS[code]) {
        try {
          const cfg = ERROR_HANDLERS[code](error);
          if (cfg) {
            renderToast(cfg);
            // Mark as handled so callers can skip their own toast
            error.handledByGlobalInterceptor = true;
          }
        } catch (renderErr) {
          // Never let the interceptor swallow the underlying error
          console.error('[apiErrorInterceptor] handler crashed', renderErr);
        }
      }
      return Promise.reject(error);
    }
  );
}

export default installApiErrorInterceptor;
